import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

interface FailedOrder {
  reference: string;
  total_price: string | null;
  external_payout_fee: string | null;
  payout_status: 'failed' | 'manual_required' | string;
  payout_provider: 'embedly' | 'paystack' | null;
  payout_reference: string | null;
  payout_failure_reason: string | null;
  payout_retry_count: number | null;
  payout_method_id: number | null;
  updated_at: string | null;
  user_id: number;
}

interface FailedAppointment {
  id: number;
  consultation_fee: string | null;
  external_payout_fee: string | null;
  payout_status: 'failed' | 'manual_required' | string;
  payout_provider: 'embedly' | 'paystack' | null;
  payout_reference: string | null;
  payout_failure_reason: string | null;
  payout_retry_count: number | null;
  payout_method_id: number | null;
  updated_at: string | null;
  consultant_id: number;
  patient_id: number;
}

interface FailedPayoutsResponse {
  orders: FailedOrder[];
  appointments: FailedAppointment[];
}

function formatNaira(value: number | string | null | undefined) {
  if (value == null) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'failed'
      ? 'bg-red-100 text-red-700'
      : status === 'manual_required'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${tone}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function ProviderBadge({ provider }: { provider: string | null }) {
  if (!provider) return <span className="text-slate-400 text-xs">—</span>;
  const color =
    provider === 'paystack'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-blue-50 text-blue-700 border-blue-200';
  return (
    <span
      className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase ${color}`}
    >
      {provider}
    </span>
  );
}

export function FailedPayoutsPage() {
  const { hasPermission } = useAuth();
  const canAct = hasPermission('orders.settle');

  const [data, setData] = useState<FailedPayoutsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<FailedPayoutsResponse>(
        '/back-office/settlements/failed'
      );
      setData(res.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load failed payouts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const retryOrder = async (ref: string) => {
    setActing(`order:${ref}:retry`);
    try {
      await api.post(`/back-office/settlements/failed/order/${ref}/retry`);
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Retry failed'));
    } finally {
      setActing(null);
    }
  };

  const markOrderManual = async (ref: string) => {
    const manual_reference =
      window.prompt(
        'Manual transfer reference (e.g., your bank statement ref) — optional:'
      ) ?? '';
    const note = window.prompt('Internal note (optional):') ?? '';
    setActing(`order:${ref}:manual`);
    try {
      await api.post(`/back-office/settlements/failed/order/${ref}/manual`, {
        manual_reference: manual_reference || undefined,
        note: note || undefined,
      });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Mark manual failed'));
    } finally {
      setActing(null);
    }
  };

  const retryAppt = async (id: number) => {
    setActing(`appt:${id}:retry`);
    try {
      await api.post(`/back-office/settlements/failed/appointment/${id}/retry`);
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Retry failed'));
    } finally {
      setActing(null);
    }
  };

  const markApptManual = async (id: number) => {
    const manual_reference = window.prompt(
      'Manual transfer reference (optional):'
    );
    const note = window.prompt('Internal note (optional):');
    setActing(`appt:${id}:manual`);
    try {
      await api.post(
        `/back-office/settlements/failed/appointment/${id}/manual`,
        {
          manual_reference: manual_reference || undefined,
          note: note || undefined,
        }
      );
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Mark manual failed'));
    } finally {
      setActing(null);
    }
  };

  const orderCount = data?.orders.length ?? 0;
  const apptCount = data?.appointments.length ?? 0;
  const total = orderCount + apptCount;

  return (
    <div className="px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Failed payouts
          </h1>
          <p className="text-sm text-slate-500">
            Settlements that errored out at the provider, or were queued for
            manual handling. Retry to fire again at the seller&apos;s default
            method, or mark as manually settled if treasury already wired the
            funds.
          </p>
        </div>
        <Link
          to="/settlements"
          className="text-sm text-brand-600 hover:underline"
        >
          ← Back to settlements
        </Link>
      </div>

      {loading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
      {!loading && error && (
        <div className="mt-6 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && total === 0 && (
        <div className="mt-6 rounded border border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          Nothing to recover. All payouts succeeded.
        </div>
      )}

      {!loading && !error && orderCount > 0 && (
        <section className="mt-6 space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Orders ({orderCount})
          </h2>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Total</th>
                  <th className="px-3 py-2">Provider</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Retries</th>
                  <th className="px-3 py-2">Updated</th>
                  <th className="px-3 py-2">Failure</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data?.orders.map((o) => (
                  <tr
                    key={o.reference}
                    className="border-t border-slate-100 align-top"
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link
                        to={`/orders/${o.reference}`}
                        className="text-brand-600 hover:underline"
                      >
                        {o.reference}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{formatNaira(o.total_price)}</td>
                    <td className="px-3 py-2">
                      <ProviderBadge provider={o.payout_provider} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={o.payout_status} />
                    </td>
                    <td className="px-3 py-2">{o.payout_retry_count ?? 0}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {formatDate(o.updated_at)}
                    </td>
                    <td
                      className="px-3 py-2 text-xs text-slate-600 max-w-[260px] truncate"
                      title={o.payout_failure_reason ?? ''}
                    >
                      {o.payout_failure_reason ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          disabled={
                            !canAct || acting === `order:${o.reference}:retry`
                          }
                          onClick={() => retryOrder(o.reference)}
                          className="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50"
                        >
                          {acting === `order:${o.reference}:retry`
                            ? 'Retrying…'
                            : 'Retry'}
                        </button>
                        <button
                          type="button"
                          disabled={
                            !canAct || acting === `order:${o.reference}:manual`
                          }
                          onClick={() => markOrderManual(o.reference)}
                          className="text-xs text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50"
                        >
                          Mark manual
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && !error && apptCount > 0 && (
        <section className="mt-6 space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Consultations ({apptCount})
          </h2>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-3 py-2">ID</th>
                  <th className="px-3 py-2">Fee</th>
                  <th className="px-3 py-2">Provider</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Retries</th>
                  <th className="px-3 py-2">Updated</th>
                  <th className="px-3 py-2">Failure</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data?.appointments.map((a) => (
                  <tr
                    key={a.id}
                    className="border-t border-slate-100 align-top"
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link
                        to={`/consultations/${a.id}`}
                        className="text-brand-600 hover:underline"
                      >
                        #{a.id}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      {formatNaira(a.consultation_fee)}
                    </td>
                    <td className="px-3 py-2">
                      <ProviderBadge provider={a.payout_provider} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={a.payout_status} />
                    </td>
                    <td className="px-3 py-2">{a.payout_retry_count ?? 0}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {formatDate(a.updated_at)}
                    </td>
                    <td
                      className="px-3 py-2 text-xs text-slate-600 max-w-[260px] truncate"
                      title={a.payout_failure_reason ?? ''}
                    >
                      {a.payout_failure_reason ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          disabled={!canAct || acting === `appt:${a.id}:retry`}
                          onClick={() => retryAppt(a.id)}
                          className="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50"
                        >
                          {acting === `appt:${a.id}:retry`
                            ? 'Retrying…'
                            : 'Retry'}
                        </button>
                        <button
                          type="button"
                          disabled={!canAct || acting === `appt:${a.id}:manual`}
                          onClick={() => markApptManual(a.id)}
                          className="text-xs text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50"
                        >
                          Mark manual
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default FailedPayoutsPage;
